import { Injectable, inject } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateHomePageInput,
    HomePage,
    ModelHomePageFilterInput,
    UpdateHomePageInput,
    DeleteHomePageInput
} from '@app/core/services/api/API.service';
import { AmplifyApiService } from './amplify-api.service';

type ListGraphqlAuthMode = 'apiKey' | 'userPool';

@Injectable({
    providedIn: 'root'
})
export class HomePageService {
    private _amplifyApiService = inject(AmplifyApiService);
    private amplifyClient = generateClient();
    private _cachedWallItems: Map<string, HomePage[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<string, Promise<HomePage[]>> = new Map();

    constructor() {}

    private _getCacheKey(filter: ModelHomePageFilterInput): string {
        return JSON.stringify(filter || {});
    }

    async listHomePages(
        filter: ModelHomePageFilterInput = null,
        authMode?: ListGraphqlAuthMode
    ): Promise<HomePage[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedWallItems.has(cacheKey)) {
            return this._cachedWallItems.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchWallItems(filter, cacheKey, authMode);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchWallItems(
        filter: ModelHomePageFilterInput,
        cacheKey: string,
        authMode?: ListGraphqlAuthMode
    ): Promise<HomePage[]> {
        try {
            const data = ((await this._amplifyApiService.graphql({
                query: queries.listHomePages,
                variables: {
                    filter
                },
                ...(authMode ? { authMode } : {})
            })) as any).data.listHomePages.items;

            if (data) {
                const wallItems = data as HomePage[];
                this._cachedWallItems.set(cacheKey, wallItems);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return wallItems;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        }
    }

    clearCache(): void {
        this._cachedWallItems.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getHomePageById(id: string): Promise<HomePage> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getHomePage,
                    variables: {
                        id
                    }
                })
            ).data.getHomePage;
            if (data) return data as HomePage;
        } catch (error) {
            console.error('Error finding HomePage:', error);
        }
    }

    async createHomePage(input: CreateHomePageInput): Promise<HomePage> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.createHomePage,
                    variables: {
                        input
                    }
                })
            ).data.createHomePage;
            if (date) return date as HomePage;
        } catch (error) {
            console.error('Error creating HomePage:', error);
            return null;
        }
    }

    async updateHomePage(input: UpdateHomePageInput): Promise<HomePage> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.updateHomePage,
                    variables: {
                        input
                    }
                })
            ).data.updateHomePage;
            if (date) return date as HomePage;
        } catch (error) {
            console.error('Error creating HomePage:', error);
            return null;
        }
    }

    async deleteHomePage(input: DeleteHomePageInput): Promise<HomePage> {
        try {
            const date = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteHomePage,
                    variables: {
                        input
                    }
                })
            ).data.deleteHomePage;
            if (date) return date as HomePage;
        } catch (error) {
            console.error('Error deleting HomePage:', error);
            return null;
        }
    }
}
